/**
 * Auth context for the admin app. Holds the current user and performs login/logout. The
 * session is persisted (token in apiClient localStorage, user here) so a page reload
 * restores it instantly for the full ~30-day token lifetime — no instant logout.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setAccessToken, getAccessToken, setOnAuthLost } from '../lib/apiClient.js';

const AuthContext = createContext(null);
const USER_KEY = 'usmehar.admin.user';

function storeUser(u) {
  try {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}
function readUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('booting'); // booting | authed | anon

  const applySession = useCallback((data) => {
    setAccessToken(data.accessToken);
    setUser(data.user);
    storeUser(data.user);
    setStatus('authed');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    setUser(null);
    storeUser(null);
    setStatus('anon');
  }, []);

  const login = useCallback(
    async (email, password) => {
      const data = await api.post('/auth/login', { email, password });
      applySession(data);
      return data.user;
    },
    [applySession],
  );

  // Force-logout hook when a transparent refresh ultimately fails.
  useEffect(() => {
    setOnAuthLost(() => {
      setAccessToken(null);
      setUser(null);
      storeUser(null);
      setStatus('anon');
    });
  }, []);

  // Bootstrap: restore a persisted session instantly; otherwise try a cookie refresh.
  useEffect(() => {
    let active = true;
    (async () => {
      const storedToken = getAccessToken();
      const storedUser = readUser();
      if (storedToken && storedUser) {
        if (active) {
          setUser(storedUser);
          setStatus('authed');
        }
        return; // token is valid up to ~30 days; the 401 interceptor handles expiry
      }
      try {
        const data = await api.post('/auth/refresh');
        if (active) applySession(data);
      } catch {
        if (active) setStatus('anon');
      }
    })();
    return () => {
      active = false;
    };
  }, [applySession]);

  const value = useMemo(
    () => ({
      user,
      status,
      isAuthed: status === 'authed',
      login,
      logout,
      hasRole: (...roles) =>
        user && (user.role === 'super_admin' || roles.flat().includes(user.role)),
    }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export default AuthProvider;
